/**
 * PickAcceptedVariants · iter303ak-fix23d — Accepted · T10 · Action 10
 * ────────────────────────────────────────────────────────────────────
 * Second-chance decision review. Pre-loads the child decisions saved by
 * Quote Sent Follow-up (Action 20 · AwaitQuoteResponse). Operator can:
 *   • Confirm the decisions as-is and advance (common case)
 *   • Alter any row (accepted ↔ declined ↔ pre-accepted with new
 *     follow-up date)
 *
 * Uses the shared QuoteDecisionPicker widget so the UX is identical to
 * the Follow-up step.
 *
 * On Confirm:
 *   • Writes updated `child_decisions` + `accepted_child_ids` +
 *     `accepted_quote_summary` + `deposit_required_total` /
 *     `contract_value_total` onto captured_data (downstream Accepted
 *     T10 A20 send_bundle + A30 deposit_capture read these).
 *   • Only PATCHes the parent appraisal if decisions actually changed
 *     vs the appraisal's stored `child_decisions`.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Layers, AlertCircle, CheckCircle2 } from 'lucide-react';
import QuoteDecisionPicker from './QuoteDecisionPicker';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const money = (n) => {
  const v = Number(n) || 0;
  return `$${v.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function PickAcceptedVariants({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;
  const app = capturedData?.appraisal || capturedData?.appraisal_record || {};

  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Seed decisions from (in order of precedence):
  //   1. renderer's own value (previous save on this action)
  //   2. captured_data.await_quote_response.decisions (upstream follow-up)
  //   3. appraisal.child_decisions on the parent doc
  //   4. empty (single-appraisal fallback covered by synth child below)
  const seedDecisions = useMemo(() => {
    return value?.decisions
      || capturedData?.decisions
      || capturedData?.await_quote_response?.decisions
      || app?.child_decisions
      || {};
  }, [value, capturedData, app]);

  const [decisions, setDecisions] = useState(seedDecisions);
  const seededRef = useRef(false);

  // If the seed becomes available AFTER first render (children load, etc)
  // hydrate the decisions map once.
  useEffect(() => {
    if (!seededRef.current && Object.keys(seedDecisions).length > 0) {
      setDecisions(seedDecisions);
      seededRef.current = true;
    }
  }, [seedDecisions]);

  const loadChildren = useCallback(async () => {
    if (!appraisalId) { setLoading(false); return; }
    try {
      // iter303ak-fix23f · Fetch fresh appraisal alongside children so
      // `child_decisions` from the just-saved Follow-up carry through.
      // The runner's local hydration is one-shot (line ~141 of
      // WorkflowRunnerPage) and doesn't refresh after PATCHes made
      // during a run — so the cached `capturedData.appraisal` was
      // stale, causing decisions to disappear here.
      const [childrenRes, freshAppRes] = await Promise.all([
        api.get(`/appraisals/${appraisalId}/children`),
        api.get(`/appraisals/${appraisalId}`).catch(() => ({ data: null })),
      ]);
      const freshApp = freshAppRes.data || app;
      let kids = Array.isArray(childrenRes.data?.children) ? childrenRes.data.children : [];
      if (kids.length === 0) {
        // Batch T follow-up · workflow integration (2026-02): read the
        // engine value first, falling back to legacy `deposit_amount`
        // for BC. Same for `quote_total` (now persisted on the doc).
        kids = [{
          id: appraisalId,
          appraisal_no: freshApp.appraisal_no || null,
          quote_type_ref: freshApp.quote_type_ref || 'This appraisal',
          variant_label: freshApp.variant_label || '',
          quote_total: freshApp.quote_total ?? 0,
          deposit_amount: freshApp.deposit_engine_amount ?? freshApp.deposit_amount ?? 0,
          deposit_engine_amount: freshApp.deposit_engine_amount ?? null,
          deposit_engine_reason: freshApp.deposit_engine_reason || '',
          _synthetic: true,
        }];
      }
      setChildren(kids);
      // Hydrate decisions from the fresh appraisal doc — takes precedence
      // over the (possibly stale) seedDecisions computed at mount time.
      const freshDecisions = freshApp?.child_decisions;
      if (freshDecisions && Object.keys(freshDecisions).length > 0 && !seededRef.current) {
        setDecisions(freshDecisions);
        seededRef.current = true;
      } else if (Object.keys(seedDecisions).length === 0 && !seededRef.current) {
        // Legacy fallback — no decisions anywhere. Auto-accept all.
        const next = {};
        for (const c of kids) {
          const cid = c.id || c._id;
          next[cid] = {
            decision: 'accepted',
            quote_type_ref: c.quote_type_ref || '',
            variant_label: c.variant_label || '',
            appraisal_no: c.appraisal_no || null,
          };
        }
        setDecisions(next);
        seededRef.current = true;
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to load quote children');
    } finally { setLoading(false); }
  }, [appraisalId, seedDecisions, app]);

  useEffect(() => { loadChildren(); }, [loadChildren]);

  // Derived state written upstream for T10 A20 (send_bundle) + A30 (deposit_capture).
  const derived = useMemo(() => {
    const acceptedChildren = children.filter((c) => decisions[c.id || c._id]?.decision === 'accepted');
    const acceptedIds = acceptedChildren.map((c) => c.id || c._id);
    const acceptedSummary = acceptedChildren
      .map((c) => [c.quote_type_ref, c.variant_label].filter(Boolean).join(' · '))
      .join('; ');
    const depositRequired = acceptedChildren.reduce(
      (acc, c) => acc + (Number(c.deposit_engine_amount ?? c.deposit_amount) || 0),
      0,
    );
    const contractValue = acceptedChildren.reduce((acc, c) => acc + (Number(c.quote_total) || 0), 0);
    return { acceptedIds, acceptedSummary, depositRequired, contractValue };
  }, [children, decisions]);

  useEffect(() => {
    if (onChange) {
      onChange({
        decisions,
        accepted_child_ids: derived.acceptedIds,
        accepted_quote_summary: derived.acceptedSummary,
        deposit_required_total: derived.depositRequired,
        contract_value_total: derived.contractValue,
      });
    }
  }, [decisions, derived.acceptedIds, derived.acceptedSummary, derived.depositRequired, derived.contractValue, onChange]);

  const advance = async () => {
    if (derived.acceptedIds.length === 0) {
      setErr('At least one quote must be marked accepted to continue in the Accepted pipeline. Change a row to Accepted, or use Back to route this appraisal to Declined via Quote Sent.');
      return;
    }
    setSaving(true); setErr('');
    try {
      if (appraisalId) {
        await api.patch(`/appraisals/${appraisalId}`, {
          child_decisions: decisions,
          accepted_child_ids: derived.acceptedIds,
          accepted_quote_summary: derived.acceptedSummary,
        });
      }
      onNext();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to save decisions');
    } finally { setSaving(false); }
  };

  // Whether decisions have been altered since upstream save.
  const altered = useMemo(() => {
    const seedKeys = Object.keys(seedDecisions);
    const currentKeys = Object.keys(decisions);
    if (seedKeys.length !== currentKeys.length) return true;
    for (const k of seedKeys) {
      if (seedDecisions[k]?.decision !== decisions[k]?.decision) return true;
      if (seedDecisions[k]?.followup_date !== decisions[k]?.followup_date) return true;
    }
    return false;
  }, [seedDecisions, decisions]);

  return (
    <div className="space-y-4" data-testid="wf-pick-accepted-variants">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
        <div className="flex items-start gap-3">
          <Layers className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-emerald-900">
              Confirm the acceptance decisions
              {altered && (
                <span className="ml-2 text-[10px] font-bold uppercase tracking-widest text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">
                  Altered from Follow-up
                </span>
              )}
            </div>
            <div className="text-sm text-emerald-800 mt-1">
              {params.prompt || 'These are the decisions captured during Quote Sent Follow-up. Confirm them, or alter any row (accepted ↔ declined ↔ pre-accepted) before you continue.'}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-sm text-slate-400 italic">Loading quote package…</div>
      ) : (
        <>
          <QuoteDecisionPicker
            children={children}
            decisions={decisions}
            onChange={setDecisions}
            headerLabel="Confirm per-quote decision"
          />
          <div className="p-3 rounded-md bg-white border border-slate-200 text-xs text-slate-600 flex items-center justify-between">
            <span>
              <b>{derived.acceptedIds.length}</b> accepted of {children.length}
            </span>
            <span>
              {derived.contractValue > 0 && <>Contract value: <b>{money(derived.contractValue)}</b></>}
              {derived.contractValue > 0 && derived.depositRequired > 0 && ' · '}
              {derived.depositRequired > 0 && <>Deposit required: <b className="text-amber-700">{money(derived.depositRequired)}</b></>}
            </span>
          </div>
        </>
      )}

      {err && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-pick-accepted-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && (
            <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-pick-accepted-cancel">Cancel</button>
          )}
          <button
            onClick={advance}
            disabled={saving || derived.acceptedIds.length === 0}
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-pick-accepted-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : `Confirm & Continue (${derived.acceptedIds.length} accepted)`}
          </button>
        </div>
      </div>
    </div>
  );
}
